// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { usePMXDeliveriesData } from "./hooks/usePMXDeliveriesData";
// --- External UI Libraries
import Button from "@dtci/spellbook/Button";
import Typography from "@dtci/spellbook/Typography";
import { Col, Row } from "antd";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import ToastNotificationProvider from "../../components/ToastNotifications";
import { PMXDeliveriesGrid } from "./components/grid";
import { PMXDeliveriesSearchForm } from "./components/search-form";
import TwoRowsLayout from "./two-row";
// --- Utilities
import { exportToCsv } from "../../utils/exportToCsv";
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- Styles
import "./styles.scss";

// Export range for CSV export
const PMX_EXPORT_RANGE = "0,999";

/**
 * PMX Deliveries Page
 *
 * Features:
 * - Displays a paginated list of PMX deliveries
 * - Provides search and export functionality
 * - Handles loading and toast notifications
 * - Uses a two-row layout for search and grid
 *
 * @returns {Element}
 * @constructor
 */
export const PMXDeliveriesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchPMXDeliveries, updatePMXDeliveries, itemsPerPage } =
    usePMXDeliveriesData();

  const { totalPages, currentPage, totalItems, pageSizeOptions } = useSelector(
    (s) => s.pmxDeliveries,
  );

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.pmxDeliveries,
    itemsPerPage,
    rangeKey: "pmxDeliveriesRange",
    onSync: updatePMXDeliveries,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchPMXDeliveries(nextPageRange);

    if (data) {
      updatePMXDeliveries({
        deliveries: data?.content,
        totalPages: data?.totalPages,
        currentPage: data?.pageable?.pageNumber,
        pmxDeliveriesRange: nextPageRange,
        totalItems: data?.totalElements,
      });
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleExport = async () => {
    const data = await fetchPMXDeliveries(PMX_EXPORT_RANGE);

    if (!data) return;

    exportToCsv(data?.content, "delivery_pmx");
  };

  return (
    <>
      <Row className="pmx-deliveries-page-header">
        <Col>
          <Typography variant="T50" className="pmx-deliveries-page-title">
            PMX Deliveries
          </Typography>
        </Col>
        <Col className="pmx-deliveries-page-actions">
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={() => updatePMXDeliveries({ refreshPMXDeliveries: true })}
          >
            Refresh
          </Button>
        </Col>
      </Row>
      <TwoRowsLayout>
        <main data-position="top">
          <PMXDeliveriesSearchForm />
        </main>
        <main data-position="bottom">
          <PMXDeliveriesGrid />
        </main>
      </TwoRowsLayout>
      <PaginationBar
        totalPages={totalPages || 0}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
